// Monta o app: estado, Pomodoro, barra flutuante (desktop), idioma, tema e título da aba.
import { useEffect, useMemo, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import type { AppEnvironment } from './data/backend';
import { detectLanguage, isLanguage, resolveLanguage, type Language } from './domain/settings';
import { initI18n } from './i18n';
import { createNotifier } from './platform/notify';
import { createSoundPlayer, type SoundPlayer } from './platform/sound';
import { AppProvider, createAppStore, useAppState } from './state/AppStore';
import { createFloatController, type FloatController } from './state/floatController';
import { createUpdateController } from './state/updateController';
import { ServicesContext, type Services } from './state/services';
import { PomodoroProvider, createPomodoroController } from './state/usePomodoro';
import { applyAutostartDefault, rememberAutostartChoice } from './platform/autostart';
import { useEstimateAlarm } from './state/useNow';
import { useTabTitle } from './state/useTabTitle';
import { useTheme } from './state/useTheme';
import { AppShell, type Layout } from './ui/AppShell';
import { UpdatePrompt } from './ui/UpdatePrompt';

function systemLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return ['en'];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

const LANG_KEY = 'beedoit.language';
const NO_FLOAT = { openFloat: async () => {}, isVisible: () => false, subscribe: () => () => {} };

/** Último idioma escolhido neste aparelho — vale enquanto o app carrega a conta; entrada e cadastro seguem o sistema (AC-121). */
function storedLanguage(): Language | null {
  try {
    const v = localStorage.getItem(LANG_KEY);
    return isLanguage(v) ? v : null;
  } catch {
    return null;
  }
}

function Effects({ sound, web }: { sound: SoundPlayer; web: boolean }) {
  const profile = useAppState((s) => s.profile);
  const signedOut = useAppState((s) => s.phase === 'signedOut');
  // entrada e cadastro sempre no idioma do aparelho (sem tradução → English) — AC-121
  const device = resolveLanguage(null, systemLanguages());
  const language = signedOut ? device : profile?.language ?? storedLanguage() ?? device;
  useEffect(() => {
    initI18n(language);
    document.documentElement.lang = language;
    if (profile?.language) {
      try {
        localStorage.setItem(LANG_KEY, profile.language);
      } catch {
        /* sem armazenamento */
      }
    }
  }, [language, profile?.language]);
  useTheme(profile?.theme ?? 'system');
  useEstimateAlarm(sound);
  return web ? <TabTitle /> : null;
}

function TabTitle() {
  useTabTitle();
  return null;
}

export interface AppProps {
  env: AppEnvironment;
  layoutOverride?: Layout;
  monitorConnectivity?: boolean;
}

export function App({ env, layoutOverride, monitorConnectivity = true }: AppProps) {
  const i18n = useMemo(() => initI18n(storedLanguage() ?? detectLanguage(systemLanguages())), []);
  const [store] = useState(() => createAppStore(env.backend));
  const sound = useMemo(() => createSoundPlayer(() => store.get().profile?.sounds_enabled !== false), [store]);
  const pomodoro = useMemo(
    () =>
      createPomodoroController({
        store,
        sound,
        notify: createNotifier(env.desktop),
        t: (k, o) => i18n.t(k, o),
        storage: env.storage,
      }),
    [store, sound, env, i18n]
  );
  // Controladores com assinaturas (eventos, timers, janela flutuante) nascem e morrem
  // dentro de efeitos: no modo de desenvolvimento o React monta, desmonta e remonta.
  useEffect(() => {
    const off = pomodoro.connect();
    const stop = pomodoro.run();
    return () => {
      off();
      stop();
    };
  }, [pomodoro]);
  const [float, setFloat] = useState<FloatController | null>(null);
  useEffect(() => {
    const ctl = createFloatController({
      store,
      bridge: env.desktop && env.desktop.label === 'main' ? env.desktop : null,
      pomodoro,
      theme: () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'),
      language: () => i18n.language,
    });
    setFloat(ctl);
    return () => ctl.dispose();
  }, [store, env, pomodoro, i18n]);

  // atualização do app desktop: verifica ao abrir e a cada 6 h (feature atualizacao)
  const updates = useMemo(() => createUpdateController(env.updater ?? null), [env]);
  useEffect(() => updates.start(), [updates]);

  // iniciar com o computador: liga uma vez por computador; nunca na versão de desenvolvimento (AC-128)
  useEffect(() => {
    if (env.desktop?.label !== 'main') return;
    void applyAutostartDefault(env.autostart ?? null, env.storage, import.meta.env.TAURI_ENV_DEBUG === 'true');
  }, [env]);

  const services: Services = useMemo(
    () => ({
      desktop: env.desktop,
      float: float ?? NO_FLOAT,
      sound,
      autostart: rememberAutostartChoice(env.autostart ?? null, env.storage),
      oauthProviders: env.oauthProviders ?? ['google', 'azure'],
      async openExternal(url: string) {
        if (env.desktop) return env.desktop.openExternal(url);
        window.location.assign(url);
      },
    }),
    [env, float, sound]
  );

  return (
    <I18nextProvider i18n={i18n}>
      <AppProvider store={store} monitorConnectivity={monitorConnectivity}>
        <PomodoroProvider controller={pomodoro}>
          <ServicesContext.Provider value={services}>
            <Effects sound={sound} web={!env.desktop} />
            <AppShell layoutOverride={layoutOverride} />
            <UpdatePrompt controller={updates} />
          </ServicesContext.Provider>
        </PomodoroProvider>
      </AppProvider>
    </I18nextProvider>
  );
}
